import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Copy, Check, MessageCircle, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { site, trackEvent } from '../data/site';
import { ebook } from '../data/content';

const WhatsAppMessage = `Hello TheBigHub! I have made payment of ₦3,999 to your First Bank account for TheBigHub Digital Marketing Playbook.

I would like to submit my payment receipt for confirmation and receive my eBook download link.

Thank you.`;

export const PaymentModal = ({ open, onClose }) => {
  const [copiedField, setCopiedField] = useState(null);

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(field === 'account' ? 'Account number copied!' : 'Payment details copied!');
      trackEvent('copy_' + field);
      setTimeout(() => setCopiedField(null), 2200);
    } catch {
      toast.error('Copy failed — please write down the details instead.');
    }
  };

  const detailsText = `Bank: FIRST BANK\nAccount Name: THE BIG HUB\nAccount Number: 2049243237\nAmount: ₦3,999`;
  const waUrl = `https://wa.me/2349138299631?text=${encodeURIComponent(WhatsAppMessage)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="payment-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-pine/80 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label="Bank transfer payment details"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow mb-2">Bank Transfer Payment</p>
                <h2 className="font-display text-2xl font-bold leading-snug text-ink">
                  You're One Step Away From Your Digital Marketing Playbook.
                </h2>
                <p className="mt-2 text-sm text-ink">
                  Complete your <strong>₦3,999</strong> bank transfer using the account details
                  below.
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="payment-modal-close"
                aria-label="Close payment popup"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-ink/15 text-ink transition-colors hover:border-forest hover:text-forest"
              >
                <X size={18} />
              </button>
            </div>

            <div className="rounded-2xl border-2 border-forest bg-forest p-5 text-white">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-goldlight">
                  <Landmark size={20} />
                </span>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-goldlight">
                  Payment Details
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">Bank Name</dt>
                  <dd className="mt-1 font-display text-lg font-bold" data-testid="modal-bank-name">FIRST BANK</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">Account Name</dt>
                  <dd className="mt-1 font-display text-lg font-bold" data-testid="modal-account-name">THE BIG HUB</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">Account Number</dt>
                  <dd className="mt-1 font-display text-2xl font-bold tracking-wider text-goldlight" data-testid="modal-account-number">
                    2049243237
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-white">Amount to Pay</dt>
                  <dd className="mt-1 font-display text-2xl font-bold text-goldlight" data-testid="modal-amount">₦3,999</dd>
                </div>
              </dl>
              <div className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <button
                  onClick={() => copy('2049243237', 'account')}
                  data-testid="modal-copy-account"
                  className="btn border-2 border-white bg-white px-4 py-3 text-sm text-forest hover:bg-goldlight hover:text-pine"
                >
                  {copiedField === 'account' ? <Check size={16} /> : <Copy size={16} />}
                  Copy Account Number
                </button>
                <button
                  onClick={() => copy(detailsText, 'details')}
                  data-testid="modal-copy-details"
                  className="btn border-2 border-white px-4 py-3 text-sm text-white hover:bg-white hover:text-forest"
                >
                  {copiedField === 'details' ? <Check size={16} /> : <Copy size={16} />}
                  Copy Payment Details
                </button>
              </div>
            </div>

            <ol className="mt-6 space-y-3">
              {[
                `Open your banking app and transfer ₦3,999 to THE BIG HUB — First Bank, Account Number 2049243237.`,
                `Once your transfer is completed, click the WhatsApp button below and send your payment receipt.`,
                `After we verify your payment, we'll send your eBook download link directly to your WhatsApp.`,
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-ink">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest font-mono text-[11px] font-semibold text-white">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>

            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="modal-whatsapp-confirm"
              onClick={() => trackEvent('whatsapp_payment_confirm_click')}
              className="btn mt-7 w-full border-2 border-[#1EBE57] bg-[#25D366] px-6 py-4 text-base font-bold text-white hover:-translate-y-0.5 hover:brightness-95"
            >
              <MessageCircle size={19} /> I'VE PAID — CONFIRM ON WHATSAPP
            </a>
            <p className="mt-3 text-center text-xs text-ink">
              WhatsApp: {site.whatsappDisplay} · You'll attach your receipt in the chat.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
